/**
 * AuthGuard - Route guard that restricts access to authenticated users only.
 *
 * Unauthenticated users are routed to LINE login:
 *   - Inside the LINE app (LIFF): via the LIFF SDK (`liff.login()`).
 *   - In a normal browser: via the LINE OAuth redirect.
 *
 * Critically, it never redirects while auth is still resolving (`isLoading`).
 * Redirecting mid-flight inside the LINE in-app browser caused a reload/redirect
 * loop (the webview re-initialised LIFF on every bounce).
 *
 * Requirements: 22.5
 */

import { useTranslation } from 'react-i18next';
import { useAuth, redirectToLineOAuth } from '@/contexts/AuthContext';
import { isLiff } from '@/services/environment';
import { liffLogin } from '@/services/liff';
import { LoadingState } from './LoadingState';

interface AuthGuardProps {
  children: React.ReactNode;
}

/** Session flag so we attempt the in-LINE LIFF login at most once per session
 *  and never spin in a redirect loop when a session can't be established. */
const LIFF_LOGIN_ATTEMPTED_KEY = 'findoryhub_liff_login_attempted';

export function AuthGuard({ children }: AuthGuardProps) {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();

  // Also check localStorage directly as a fallback (AuthContext may not have
  // finished validating the token yet).
  const hasStoredToken =
    !!localStorage.getItem('findoryhub_session_token') ||
    !!localStorage.getItem('findoryhub_refresh_token') ||
    !!localStorage.getItem('findoryhub_session'); // legacy key

  // Auth is still resolving (token validation, or LIFF init/login in the LINE
  // app). Wait — do NOT redirect here, or the LIFF webview loops.
  if (isLoading) {
    return <LoadingState variant="spinner" />;
  }

  if (isAuthenticated || hasStoredToken) {
    // Clear the one-shot flag so a later logout can re-trigger LIFF login.
    try {
      sessionStorage.removeItem(LIFF_LOGIN_ATTEMPTED_KEY);
    } catch {
      // sessionStorage unavailable — non-fatal
    }
    return <>{children}</>;
  }

  // Unauthenticated and finished loading: route to login.
  try {
    sessionStorage.setItem('findoryhub_redirect', window.location.pathname);
  } catch {
    // ignore
  }

  if (isLiff()) {
    // Inside LINE: authenticate through the LIFF SDK, never the browser OAuth
    // redirect (which loops in the in-app webview). Attempt once; if a session
    // still can't be established (e.g. the LIFF app lacks the OpenID scope so
    // no ID token is issued), stop and show a retry affordance instead of
    // looping.
    let attempted = false;
    try {
      attempted = !!sessionStorage.getItem(LIFF_LOGIN_ATTEMPTED_KEY);
      sessionStorage.setItem(LIFF_LOGIN_ATTEMPTED_KEY, '1');
    } catch {
      // sessionStorage unavailable — fall through to a single attempt
    }

    if (!attempted) {
      liffLogin();
      return <LoadingState variant="spinner" />;
    }

    // Already tried and still no session — avoid an infinite loop.
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-gray-600">
          {t(
            'auth.liffLoginFailed',
            'We could not sign you in through LINE. Please try again.',
          )}
        </p>
        <button
          type="button"
          onClick={() => {
            try {
              sessionStorage.removeItem(LIFF_LOGIN_ATTEMPTED_KEY);
            } catch {
              // ignore
            }
            liffLogin();
          }}
          className="min-h-[44px] rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-primary-700"
        >
          {t('common.retry', 'Retry')}
        </button>
      </div>
    );
  }

  redirectToLineOAuth();
  return <LoadingState variant="spinner" />;
}
